import React from 'react'
import * as Sentry from '@sentry/react'
import { PostHogProvider } from '@posthog/react'
import posthog from 'posthog-js'

import { createRoot } from 'react-dom/client'
import { PrivyProvider } from '@privy-io/react-auth'
import { HelmetProvider } from 'react-helmet-async'
import App from './App'
import './index.css'

import { defineChain } from 'viem'

// Initialize Sentry error tracking
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0,    // Don't record all sessions
  replaysOnErrorSampleRate: 1.0,  // Record 100% of sessions with errors
  enabled: import.meta.env.PROD,
})

// Initialize PostHog — must happen before PostHogProvider so the
// singleton imported by analytics.ts is the same initialized instance.
posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  person_profiles: 'identified_only',
})

export const polygonZkEvmCardonaTestnet = defineChain({
  id: 2442,
  name: 'Polygon zkEVM Cardona Testnet',
  network: 'polygon',
  nativeCurrency: {
    decimals: 18,
    name: 'Ethereum',
    symbol: 'ETH',
  },
  rpcUrls: {
    default: {
      http: ['https://etherscan.cardona.zkevm-rpc.com'],
    },
  },
  blockExplorers: {
    default: {
      name: 'PolygonScan',
      url: 'https://cardona-zkevm.polygonscan.com',
    },
  },
})

//  Theta Mainnet chain
export const thetaMainnet = defineChain({
  id: 361,
  name: 'Theta Mainnet',
  network: 'theta',
  nativeCurrency: {
    decimals: 18,
    name: 'Theta Fuel',
    symbol: 'TFUEL',
  },
  rpcUrls: {
    default: {
      http: ['https://eth-rpc-api.thetatoken.org/rpc'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Theta Explorer',
      url: 'https://explorer.thetatoken.org/',
    },
  },
})

const customBNBNetwork = defineChain({
  id: 97,
  name: 'BNB Smart Chain Testnet',
  network: 'binance',
  nativeCurrency: {
    decimals: 18,
    name: 'BNB',
    symbol: 'BNB',
  },
  rpcUrls: {
    default: {
      http: ['https://data-seed-prebsc-1-s1.binance.org:8545/'],
    },
  },
  blockExplorers: {
    default: {
      name: 'BscScan',
      url: 'https://testnet.bscscan.com/',
    },
  },
})

const customHoleskyNetwork = defineChain({
  id: 17000,
  name: 'Holesky Test Network',
  network: 'holesky',
  nativeCurrency: {
    decimals: 18,
    name: 'Holesky',
    symbol: 'ETH',
  },
  rpcUrls: {
    default: {
      http: [
        'https://eth-holesky.g.alchemy.com/v2/3l1FowDVfzUhd5gaW7-Z_9f_lTOhgXCU',
      ],
    },
  },
  blockExplorers: {
    default: {
      name: 'HoleskyScan',
      url: 'https://holesky.etherscan.io/',
    },
  },
})

// Base Mainnet for USDC
export const baseMainnet = defineChain({
  id: 8453,
  name: 'Base',
  network: 'base',
  nativeCurrency: {
    decimals: 18,
    name: 'Ethereum',
    symbol: 'ETH',
  },
  rpcUrls: {
    default: {
      http: ['https://mainnet.base.org'],
    },
  },
  blockExplorers: {
    default: {
      name: 'BaseScan',
      url: 'https://basescan.org',
    },
  },
})

createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <HelmetProvider>
        <PrivyProvider
          appId={import.meta.env.VITE_APP_PRIVY_APP_ID ?? ''}
          config={{
            // Display email and wallet as login methods
            loginMethods: ['email', 'google'],
            // Customize Privy's appearance in your app
            defaultChain: baseMainnet,
            supportedChains: [baseMainnet, customHoleskyNetwork],
            appearance: {
              theme: 'dark',
              accentColor: '#02f305',
              logo: 'https://cyberlete.net/logo.png',
              // Landing page shows login options
              landingHeader: 'Sign in to Cyberlete',
              loginMessage: 'Join the ultimate gaming platform',
              // Mobile-friendly modal styling
              walletChainType: 'ethereum-only',
            },
            // Create embedded wallets for users who don't have a wallet
            embeddedWallets: {
              createOnLogin: 'users-without-wallets',
            } as any,
          }}
        >
          <App />
        </PrivyProvider>
      </HelmetProvider>
    </PostHogProvider>
  </React.StrictMode>,
)
